<template>
    <Button
        v-bind="$attrs"
        :severity="isSelected ? severity : 'secondary'"
        :variant="!isSelected ? 'outlined' : 'solid'"
        @click="handleClick"
    >
        <slot />
    </Button>
</template>

<script>
import { getPrimeVueSeverity } from './utils/componentMappings.js';

/**
 * ERadioButton - Abstraction wrapper around PrimeVue's Button
 *
 * Usage:
 *   <e-field>
 *     <e-radio-button v-model="selected" native-value="option1">
 *       Option 1
 *     </e-radio-button>
 *     <e-radio-button v-model="selected" native-value="option2">
 *       Option 2
 *     </e-radio-button>
 *   </e-field>
 *
 *   <e-field grouped>
 *     <e-radio-button v-model="size" native-value="small" type="is-success">
 *       Small
 *     </e-radio-button>
 *     <e-radio-button v-model="size" native-value="large" type="is-danger">
 *       Large
 *     </e-radio-button>
 *   </e-field>
 *
 * Buefy Props (work in both modes):
 *   - v-model: The value binding for the radio button group
 *   - native-value: The value this button represents
 *   - type: Button type/color (e.g., 'is-primary', 'is-success is-outlined')
 *   - disabled: Boolean (disables button)
 *
 * Property Mappings (Buefy → PrimeVue):
 *   - v-model → Managed internally, emits 'update:modelValue'
 *   - native-value → Used for comparison with modelValue
 *   - type → severity (with outlined support)
 *   - 'is-outlined' in type → outlined prop
 */
export default {
    name: 'ERadioButton',
    inheritAttrs: false,
    computed: {
        modelValue() {
            return this.$attrs.modelValue;
        },
        nativeValue() {
            return this.$attrs['native-value'];
        },
        isSelected() {
            return this.modelValue === this.nativeValue;
        },
        typeString() {
            const { type } = this.$attrs;
            if (typeof type === 'string') {
                return type;
            }
            return '';
        },
        severity() {
            // Extract the first color type from the type string
            const types = this.typeString.split(' ');
            const mappedSeverity = types.map(type => getPrimeVueSeverity(type)).find(severity => severity);

            return mappedSeverity || 'primary';
        },
    },
    methods: {
        handleClick() {
            if (this.$attrs.disabled) return;

            // Emit the update:modelValue event with the native-value
            const emit = this.$attrs['onUpdate:modelValue'];
            if (emit) {
                emit(this.nativeValue);
            }
        },
    },
};
</script>

<style scoped>
.p-button-outlined.p-button-contrast {
    border: 1px solid var(--border);
}
.p-button-secondary{
    background-color: var(--p-surface-0);
}
</style>
